import { useState } from "react";
import { DatePicker, ConfigProvider, theme as antdTheme } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTheme } from "next-themes";
import {
  Users,
  TrendingUp,
  Activity,
  Smartphone,
  ArrowUpRight,
  ArrowDownRight,
  Utensils,
  Heart,
  Camera,
  Clock,
  Star,
  Zap,
  LogIn,
  type LucideIcon,
} from "lucide-react";
import { useDashboardStats, useSignupsChart, useFeatureUsage, useDAUBreakdown, usePeakUsageHours, useTopUsers, useRecentActivity } from "../../hooks/useApi";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";

const getActivityIcon = (action: string): LucideIcon => {
  const a = action.toLowerCase();
  if (a.includes("analyze_food_image") || a.includes("camera") || a.includes("scan")) return Camera;
  if (a.includes("logged in") || a.includes("login") || a.includes("sign in")) return LogIn;
  if (a.includes("meal") || a.includes("breakfast") || a.includes("lunch") || a.includes("dinner") || a.includes("food")) return Utensils;
  if (a.includes("glucose") || a.includes("spike")) return Zap;
  if (a.includes("step") || a.includes("goal")) return Activity;
  if (a.includes("onboard")) return Star;
  if (a.includes("score") || a.includes("bite balance")) return Heart;
  if (a.includes("recipe") || a.includes("coaching")) return Utensils;
  return Activity;
};



const AdminDashboard = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [showAllActivity, setShowAllActivity] = useState(false);

  const dateParam = selectedDate ? selectedDate.format("YYYY-MM-DD") : undefined;

  // Fetch dashboard stats from API
  const { data: dashboardData, isLoading, isError, error } = useDashboardStats(dateParam);
  
  // Fetch signups chart data from API
  const { data: signupData, isLoading: isSignupsLoading } = useSignupsChart(dateParam);
  
  // Fetch feature usage data from API
  const { data: featureUsage, isLoading: isFeatureLoading } = useFeatureUsage(dateParam);
  
  // Fetch DAU breakdown data from API
  const { data: dailyActiveData, isLoading: isDAULoading } = useDAUBreakdown(dateParam);
  
  // Fetch peak usage hours data from API
  const { data: hourlyUsage, isLoading: isPeakUsageLoading } = usePeakUsageHours(dateParam);
  
  // Fetch top users data from API
  const { data: topUsers, isLoading: isTopUsersLoading } = useTopUsers(dateParam);

  // Fetch recent activity data from API
  const { data: recentActivityData, isLoading: isActivityLoading } = useRecentActivity(dateParam);

  // Map API metrics to display format with icons
  const getMetricIcon = (id: string) => {
    switch (id) {
      case "total-users":
        return Users;
      case "daily-active-users":
        return Activity;
      case "fitbit-connected":
        return Activity;
      default:
        return Users;
    }
  };

  const statsCards = dashboardData?.metrics.map((metric) => ({
    title: metric.label,
    value: metric.value.toLocaleString(),
    change: metric.change,
    trend: metric.change_type === "positive" ? ("up" as const) : metric.change_type === "negative" ? ("down" as const) : ("neutral" as const),
    icon: getMetricIcon(metric.id),
    description: metric.subtext,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Overview of BiteBalance app performance & usage
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ConfigProvider
            theme={{
              algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
              token: {
                colorPrimary: isDark ? "hsl(221, 83%, 53%)" : "hsl(221, 65%, 58%)",
                colorBgContainer: isDark ? "hsl(224, 71%, 4%)" : "hsl(0, 0%, 100%)",
                colorBgElevated: isDark ? "hsl(224, 71%, 4%)" : "hsl(0, 0%, 100%)",
                colorText: isDark ? "hsl(210, 40%, 98%)" : "hsl(240, 10%, 10%)",
                colorTextPlaceholder: isDark ? "hsl(215, 20%, 65%)" : "hsl(240, 4%, 46%)",
                colorBorder: isDark ? "hsl(224, 71%, 15%)" : "hsl(240, 6%, 90%)",
                borderRadius: 8,
              },
            }}
          >
            <DatePicker
              format="MMM D, YYYY"
              value={selectedDate}
              onChange={setSelectedDate}
              allowClear
            />
          </ConfigProvider>
          <Badge variant="outline" className="text-xs">
            <Clock className="mr-1 h-3 w-3" />
            Last updated: just now
          </Badge>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-3">
        {isLoading ? (
          // Loading skeleton
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="relative overflow-hidden">
              <CardContent className="p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Skeleton className="h-8 w-8 rounded-lg" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-8 w-20" />
                <div className="mt-1 flex items-center justify-between">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-4 w-12" />
                </div>
              </CardContent>
            </Card>
          ))
        ) : isError ? (
          // Error state
          <Card className="col-span-3">
            <CardContent className="p-4">
              <p className="text-sm text-destructive">
                Failed to load dashboard data. Please try again.
              </p>
            </CardContent>
          </Card>
        ) : (
          // Data loaded successfully
          statsCards.map(stat => (
            <Card key={stat.title} className="relative overflow-hidden">
              <CardContent className="p-4">
                <div className="mb-2 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                    <stat.icon className="h-4 w-4 text-primary" />
                  </div>
                  <p className="text-xs font-medium text-muted-foreground">
                    {stat.title}
                  </p>
                </div>
                <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-[11px] text-muted-foreground">
                    {stat.description}
                  </p>
                  <span
                    className={`flex items-center gap-0.5 text-xs font-semibold ${
                      stat.trend === "up" ? "text-emerald-600" : stat.trend === "down" ? "text-red-500" : "text-muted-foreground"
                    }`}
                  >
                    {stat.trend === "up" ? (
                      <ArrowUpRight className="h-3 w-3" />
                    ) : stat.trend === "down" ? (
                      <ArrowDownRight className="h-3 w-3" />
                    ) : null}
                    {stat.change}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Charts Row 1: Signups + Feature Usage */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2 border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">User Signups vs Target</CardTitle>
            <CardDescription>
              Monthly new registrations compared to goals
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64 border-0 outline-none">
              {isSignupsLoading ? (
                <div className="flex h-full items-center justify-center">
                  <Skeleton className="h-full w-full" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={signupData || []}>
                    <defs>
                      <linearGradient
                        id="signupGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="hsl(var(--primary))"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="hsl(var(--primary))"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                    />
                    <XAxis
                      dataKey="name"
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 12,
                      }}
                    />
                    <YAxis
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 12,
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        color: "hsl(var(--foreground))",
                      }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="Signups"
                      name="Signups"
                      stroke="hsl(var(--primary))"
                      fill="url(#signupGradient)"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="Target"
                      name="Target"
                      stroke="hsl(var(--muted-foreground))"
                      strokeDasharray="5 5"
                      strokeWidth={1.5}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Feature Usage</CardTitle>
            <CardDescription>User engagement by feature</CardDescription>
          </CardHeader>
          <CardContent>
            {isFeatureLoading ? (
              <div className="flex h-64 items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : (
              <>
                <div className="h-48 border-0 outline-none">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={featureUsage || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={4}
                        dataKey="value"
                      >
                        {(featureUsage || []).map((item, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={item.color}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                          color: "hsl(var(--foreground))",
                        }}
                        formatter={(value: number) => [`${value.toFixed(1)}%`, ""]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {(featureUsage || []).map((item) => (
                    <div
                      key={item.name}
                      className="flex items-center gap-2 text-xs"
                    >
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="truncate text-muted-foreground">
                        {item.name}
                      </span>
                      <span className="ml-auto font-semibold text-foreground">
                        {item.value.toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: DAU Breakdown + Peak Hours */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Daily Active Users Breakdown
            </CardTitle>
            <CardDescription>Returning vs new users this week</CardDescription>
          </CardHeader>
          <CardContent>
            {isDAULoading ? (
              <div className="flex h-56 items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : (
              <div className="h-56 border-0 outline-none">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyActiveData || []}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                    />
                    <XAxis
                      dataKey="name"
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 12,
                      }}
                    />
                    <YAxis
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 12,
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        color: "hsl(var(--foreground))",
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey="Returning"
                      name="Returning"
                      fill="hsl(var(--primary))"
                      radius={[4, 4, 0, 0]}
                      stackId="a"
                    />
                    <Bar
                      dataKey="New"
                      name="New"
                      fill="hsl(var(--chart-2))"
                      radius={[4, 4, 0, 0]}
                      stackId="a"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Peak Usage Hours</CardTitle>
            <CardDescription>
              When users are most active throughout the day
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isPeakUsageLoading ? (
              <div className="flex h-56 items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : (
              <div className="h-56 border-0 outline-none">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={hourlyUsage || []}>
                    <defs>
                      <linearGradient
                        id="hourlyGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="hsl(var(--chart-3))"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="hsl(var(--chart-3))"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                    />
                    <XAxis
                      dataKey="name"
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 11,
                      }}
                    />
                    <YAxis
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 12,
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        color: "hsl(var(--foreground))",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="hsl(var(--chart-3))"
                      fill="url(#hourlyGradient)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Top Users + Recent Activity */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Top Users */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top Users</CardTitle>
            <CardDescription>Most engaged users this month</CardDescription>
          </CardHeader>
          <CardContent>
            {isTopUsersLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-4 w-4 rounded" />
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-3 w-3/4 rounded" />
                      <Skeleton className="h-2 w-1/2 rounded" />
                    </div>
                    <Skeleton className="h-5 w-8 rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {(topUsers ?? []).map((user, i) => (
                  <div key={user.id} className="flex items-center gap-3">
                    <span className="w-4 text-xs font-bold text-muted-foreground">
                      #{i + 1}
                    </span>
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                      {user.initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {user.name}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {user.subtext}
                      </p>
                    </div>
                    <span className="text-xs font-bold text-primary">
                      {user.score}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recent Activity</CardTitle>
            <CardDescription>Live user actions feed</CardDescription>
          </CardHeader>
          <CardContent>
            {isActivityLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-7 w-7 rounded-lg" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-3 w-full rounded" />
                    </div>
                    <Skeleton className="h-2 w-12 rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-0">
                {(recentActivityData ?? []).slice(0, showAllActivity ? undefined : 5).map((activity, index, array) => {
                  const Icon = getActivityIcon(activity.action);
                  const isLast = index === array.length - 1;
                  return (
                    <div key={activity.id} className={`flex items-center gap-3 py-3 ${!isLast ? 'border-b border-primary/20' : ''}`}>
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground">
                          <span className="font-semibold">{activity.user}</span>{" "}
                          <span className="font-normal text-muted-foreground">{activity.action}</span>
                        </p>
                      </div>
                      <span className="whitespace-nowrap text-xs text-muted-foreground">
                        {activity.time}
                      </span>
                    </div>
                  );
                })}
                {(recentActivityData ?? []).length > 5 && (
                  <button
                    onClick={() => setShowAllActivity((prev) => !prev)}
                    className="w-full pt-1 text-xs font-medium text-primary hover:underline"
                  >
                    {showAllActivity ? "View Less" : `View More (${(recentActivityData ?? []).length - 5} more)`}
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
